import { useState } from "react";
import PageContainer from "../../uikit/PageContainer/PageContainer";
import TeamPersonList from "./components/TeamPersonList";
import AddPersonForm from "./components/AddPersonFormDrawer";
import TeamDetailsForm from "./components/TeamDetailsForm";
import {
  useDeletePerson,
  useFetchRegister,
  useFetchTeamPersons,
  useRegisterTeam,
  useSavePerson,
} from "../../api/hooks";
import { FieldValues } from "react-hook-form";
import { SavePersonInput } from "../../api/types";
import { classNames } from "../../utils";
import { HelloAssoButton } from "../../uikit/Checkout/HelloAssoButton";
import Stepper from "../../uikit/Stepper/Stepper";
import { helloAssoAPI } from "../../api/helloasso";
import CheckoutDialog from "../../uikit/Checkout/CheckoutDialog";
import { useSearchParams } from "react-router-dom";

const getRegisterStep = ({
  isTeamRegistered,
  hasPaid,
  hasBeenValidated,
}: {
  isTeamRegistered: boolean;
  hasPaid: boolean;
  hasBeenValidated: boolean;
}) => {
  if (!isTeamRegistered) {
    return 0;
  }
  if (!hasPaid) {
    return 1;
  }
  if (!hasBeenValidated) {
    return 2;
  }
  return 3;
};

export default function PageRegister() {
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [selectedRole, setSelectedRole] = useState<"staff" | "player">(
    "player"
  );
  const [selectedPerson, setSelectedPerson] = useState<string | undefined>();
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | undefined>();

  const [searchParams] = useSearchParams();
  const teamId = searchParams.get("team");

  const { data: teamDetails } = useFetchRegister(teamId);
  const { data: teamPersons } = useFetchTeamPersons(teamId);

  const isTeamRegistered = Boolean(teamDetails?.id);
  const hasPaid = Boolean(teamDetails?.fields.order_id);
  const hasBeenValidated = Boolean(teamDetails?.fields.validated);
  const currentStep = getRegisterStep({
    isTeamRegistered,
    hasPaid,
    hasBeenValidated,
  });

  const registerTeam = useRegisterTeam();
  const savePerson = useSavePerson();
  const deletePerson = useDeletePerson();

  const players =
    teamPersons
      ?.filter((person) => person.fields.role === "player")
      .map((record) => ({
        ...record.fields,
        id: record.id,
      })) || [];
  const staff =
    teamPersons
      ?.filter((person) => person.fields.role !== "player")
      .map((record) => ({
        ...record.fields,
        id: record.id,
      })) || [];

  const onClickAddPerson = (role: "staff" | "player") => {
    setShowAddPerson(true);
    setSelectedRole(role);
  };

  const onClickSavePerson = async (person: SavePersonInput) => {
    if (!teamId) {
      return;
    }
    try {
      await savePerson.mutateAsync({
        ...person,
        team_register: [teamId],
      });
    } catch (error) {
      console.error(error);
    }
  };

  const onClickEditPerson = (id?: string) => {
    if (!id) {
      return;
    }
    const person = teamPersons?.find((record) => record.id === id);
    if (!person) {
      return;
    }
    setShowAddPerson(true);
    setSelectedRole(person.fields.role);
    setSelectedPerson(id);
  };

  const onClickRemovePerson = (id?: string) => {
    if (!id) {
      return;
    }
    deletePerson.mutate(id);
  };

  const onSubmit = async (data: FieldValues) => {
    const hasAlreadyBeenRegistered = Boolean(teamId);
    return registerTeam
      .mutateAsync({
        ...(teamId && { recordId: teamId }),
        ...data,
      })
      .then((res) => {
        if (!hasAlreadyBeenRegistered) {
          const recordId = res?.id;
          window.location.href = `/register?team=${recordId}`;
        }
      });
  };

  const onProcessPayment = async () => {
    setIsProcessingPayment(true);
    try {
      const result = await helloAssoAPI.initCart({
        id: teamId || "",
        firstname: teamDetails?.fields.responsible_first_name || "",
        lastname: teamDetails?.fields.responsible_last_name || "",
        email: teamDetails?.fields.email || "",
        teamName: teamDetails?.fields.team_name || "",
        method: 1,
        amount: 850,
      });
      if ("redirectUrl" in result) {
        setCheckoutUrl(result.redirectUrl);
      } else {
        console.error("Error initializing cart:", result.error);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const onCancelPayment = () => {
    setCheckoutUrl(undefined);
  };

  return (
    <PageContainer title={""}>
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-[-10rem] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[-20rem]"
      >
        <div
          style={{
            clipPath:
              "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
          }}
          className="relative left-1/2 -z-10 aspect-[1155/678] w-[36.125rem] max-w-none -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-40rem)] sm:w-[72.1875rem]"
        />
      </div>
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-balance text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
          Inscription
        </h2>
        <p className="mt-2 text-lg/8 text-gray-600">
          Merci de remplir ce formulaire pour vous inscrire au tournoi.
        </p>
        {isTeamRegistered && (
          <span className="inline-flex items-center gap-x-1.5 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
            <svg
              viewBox="0 0 6 6"
              aria-hidden="true"
              className="w-1.5 h-1.5 fill-green-500"
            >
              <circle r={3} cx={3} cy={3} />
            </svg>
            Enregistré
          </span>
        )}
        {isTeamRegistered && (
          <div className="mt-4 bg-green-100 p-4 rounded-lg">
            <p className="text-sm/6 text-green-700">
              Votre équipe a bien été enregistrée.
            </p>
            <p className="text-sm/6 text-green-700">
              Pour modifier ultérieurement, utilisez le lien suivant :<br />
              <a
                href={window.location.href}
                className="text-green-700 underline font-bold"
              >
                {window.location.href}
              </a>
            </p>
          </div>
        )}
        {isTeamRegistered && !hasPaid && (
          <div onClick={onProcessPayment}>
            <HelloAssoButton isLoading={isProcessingPayment} />
          </div>
        )}
      </div>
      <Stepper currentStep={currentStep} />
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
        <div className="grid grid-cols-1 gap-4 bg-gray px-3 pb-12 sm:pb-16 lg:px-6">
          <TeamDetailsForm
            initialData={teamDetails}
            onSubmit={onSubmit}
            isSubmitting={registerTeam.isLoading}
          />
        </div>
        <div
          className={classNames(
            "grid grid-cols-1 gap-4 bg-gray px-3 py-12 sm:py-16 lg:px-6",
            !teamId ? "opacity-50 pointer-events-none" : ""
          )}
        >
          <div className="flex flex-col gap-y-2">
            <div>
              <h3 className="text-lg/8 font-semibold text-gray-900">Staff</h3>
              <p className="text-sm/6 text-gray-600">
                Ajoutez les membres de votre équipe.
              </p>
            </div>
            <TeamPersonList
              role="staff"
              persons={staff}
              onAddPerson={() => onClickAddPerson("staff")}
              onEditPerson={onClickEditPerson}
              onRemovePerson={(id) => onClickRemovePerson(id)}
            />
          </div>
          <div className="flex flex-col gap-y-2">
            <div>
              <h3 className="text-lg/8 font-semibold text-gray-900">Joueurs</h3>
              <p className="text-sm/6 text-gray-600">
                Ajoutez les membres de votre équipe.
              </p>
            </div>
            <TeamPersonList
              role="player"
              persons={players}
              onAddPerson={() => onClickAddPerson("player")}
              onEditPerson={onClickEditPerson}
              onRemovePerson={(id) => onClickRemovePerson(id)}
            />
          </div>
        </div>
      </div>
      <AddPersonForm
        role={selectedRole}
        open={showAddPerson}
        teamId={teamDetails?.id || ""}
        initialData={
          selectedPerson
            ? teamPersons?.find((record) => record.id === selectedPerson)
                ?.fields
            : undefined
        }
        setOpen={setShowAddPerson}
        onSavePerson={onClickSavePerson}
      />
      <CheckoutDialog
        checkoutUrl={checkoutUrl || ""}
        onCancel={onCancelPayment}
      />
    </PageContainer>
  );
}
